import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Save, Loader2, Pencil, X, ExternalLink, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api, resolveImg, formatApiError } from "@/lib/api";
import AdminField from "@/components/admin/AdminField";
import Logo from "@/components/site/Logo";
import Seo from "@/components/site/Seo";

const SCHEMAS = {
  services: { label: "Servizi", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo" }, { key: "slug", label: "Slug (URL)" },
    { key: "icona", label: "Icona (Scissors, Sprout, TreeDeciduous, Axe, Shovel, ClipboardCheck)" },
    { key: "breve", label: "Descrizione breve", type: "textarea" },
    { key: "descrizione", label: "Descrizione completa", type: "textarea" },
    { key: "per_chi", label: "A chi è rivolto", type: "textarea" },
    { key: "esigenze", label: "Esigenze risolte", type: "list" },
    { key: "immagine", label: "Immagine", type: "image" },
    { key: "ordine", label: "Ordine", type: "number" },
  ]},
  works: { label: "Lavori", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo" }, { key: "comune", label: "Comune" }, { key: "categoria", label: "Categoria" },
    { key: "descrizione", label: "Descrizione", type: "textarea" }, { key: "data", label: "Data" },
    { key: "immagine", label: "Immagine principale", type: "image" }, { key: "alt", label: "Testo alternativo (SEO)" },
    { key: "placeholder", label: "Contenuto segnaposto (demo)", type: "bool" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  "before-after": { label: "Prima e dopo", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo" }, { key: "comune", label: "Comune" }, { key: "servizio", label: "Servizio" },
    { key: "descrizione", label: "Descrizione", type: "textarea" },
    { key: "prima", label: "Immagine PRIMA", type: "image" }, { key: "dopo", label: "Immagine DOPO", type: "image" },
    { key: "placeholder", label: "Segnaposto (demo)", type: "bool" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  zones: { label: "Zone servite", titleKey: "nome", fields: [
    { key: "nome", label: "Nome comune" }, { key: "slug", label: "Slug" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  faqs: { label: "FAQ", titleKey: "domanda", fields: [
    { key: "domanda", label: "Domanda" }, { key: "risposta", label: "Risposta", type: "textarea" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  blog: { label: "Blog", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo (H1)" }, { key: "slug", label: "Slug (URL)" }, { key: "categoria", label: "Categoria" },
    { key: "estratto", label: "Estratto / anteprima", type: "textarea" },
    { key: "content_html", label: "Contenuto HTML (usa <h2>, <p>, ...)", type: "textarea" },
    { key: "meta_title", label: "Meta title (SEO)" }, { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
    { key: "immagine", label: "Immagine", type: "image" }, { key: "data", label: "Data (AAAA-MM-GG)" },
    { key: "pubblicato", label: "Pubblicato", type: "bool" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  reviews: { label: "Recensioni", titleKey: "autore", fields: [
    { key: "autore", label: "Autore" }, { key: "testo", label: "Testo recensione", type: "textarea" },
    { key: "rating", label: "Stelle (1-5)", type: "number" }, { key: "data", label: "Data (es. '3 anni fa')" },
    { key: "source", label: "Fonte (es. Google)" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  gallery: { label: "Galleria", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo" }, { key: "categoria", label: "Categoria (opzionale)" },
    { key: "immagine", label: "Foto", type: "image" }, { key: "alt", label: "Testo alternativo (SEO)" },
    { key: "ordine", label: "Ordine", type: "number" },
  ]},
  guides: { label: "Guide", titleKey: "titolo", fields: [
    { key: "titolo", label: "Titolo (H1)" }, { key: "slug", label: "Slug (URL)" },
    { key: "categoria", label: "Slug categoria (es. prato, potature)" },
    { key: "domanda", label: "Domanda" }, { key: "risposta_breve", label: "Risposta breve", type: "textarea" },
    { key: "corpo", label: "Approfondimento", type: "textarea" },
    { key: "cause", label: "Cause frequenti", type: "list" },
    { key: "fai_da_te", label: "Cosa puo fare da solo", type: "list" },
    { key: "errori", label: "Errori da evitare", type: "list" },
    { key: "quando_professionista", label: "Quando rivolgersi a un professionista", type: "textarea" },
    { key: "sicurezza", label: "Avvertenza di sicurezza", type: "textarea" },
    { key: "servizio", label: "Servizio correlato (testo)" },
    { key: "guide_correlate", label: "Slug guide correlate", type: "list" },
    { key: "keywords", label: "Parole chiave", type: "list" },
    { key: "meta_title", label: "Meta title (SEO)" }, { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
    { key: "immagine", label: "Immagine", type: "image" },
    { key: "data_pubblicazione", label: "Data pubblicazione" }, { key: "data_aggiornamento", label: "Data aggiornamento" },
    { key: "autore", label: "Autore" }, { key: "pubblicato", label: "Pubblicato", type: "bool" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  "guide-categories": { label: "Categorie guide", titleKey: "nome", fields: [
    { key: "nome", label: "Nome" }, { key: "slug", label: "Slug (URL)" },
    { key: "descrizione", label: "Descrizione", type: "textarea" },
    { key: "immagine", label: "Immagine", type: "image" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
  "local-pages": { label: "Pagine locali", titleKey: "comune", fields: [
    { key: "comune", label: "Comune (nome visualizzato)" }, { key: "slug", label: "Slug (URL, es. ferno)" },
    { key: "hero_titolo", label: "Titolo pagina (H1)" },
    { key: "intro", label: "Introduzione (separa i paragrafi con una riga vuota)", type: "textarea" },
    { key: "highlights", label: "Punti in evidenza", type: "list" },
    { key: "servizi", label: "Servizi in evidenza (titoli esatti dei servizi)", type: "list" },
    { key: "meta_title", label: "Meta title (SEO)" }, { key: "meta_description", label: "Meta description (SEO)", type: "textarea" },
    { key: "pubblicato", label: "Pubblicato", type: "bool" }, { key: "ordine", label: "Ordine", type: "number" },
  ]},
};

const DATA_KEY = { services: "services", works: "works", "before-after": "beforeAfter", zones: "zones", faqs: "faqs", blog: "blog", reviews: "reviews", gallery: "gallery", guides: "guides", "guide-categories": "guideCategories", "local-pages": "localPages" };

const SETTINGS_FIELDS = [
  { key: "telefono", label: "Telefono (visualizzato)" }, { key: "telefono_link", label: "Telefono (link, es. +39...)" },
  { key: "whatsapp", label: "Link WhatsApp base" }, { key: "email", label: "Email" }, { key: "sito", label: "Sito web" },
  { key: "slogan", label: "Slogan" }, { key: "whatsapp_msg", label: "Messaggio WhatsApp", type: "textarea" },
  { key: "whatsapp_msg_float", label: "Messaggio WhatsApp (pulsante flottante)", type: "textarea" },
  { key: "hero_titolo", label: "Titolo Hero" }, { key: "hero_sottotitolo", label: "Sottotitolo Hero", type: "textarea" },
  { key: "presentazione_titolo", label: "Titolo presentazione" }, { key: "presentazione_testo", label: "Testo presentazione", type: "textarea" },
  { key: "about_testo", label: "Testo Chi siamo", type: "textarea" }, { key: "recensioni_placeholder", label: "Testo recensioni (se vuote)" },
  { key: "partita_iva", label: "Partita IVA (footer)" }, { key: "indirizzo", label: "Indirizzo (footer)" },
  { key: "ragione_sociale", label: "Ragione sociale" },
  { key: "facebook", label: "Link pagina Facebook" },
  { key: "google_business", label: "Link Google Business / Recensioni" },
  { key: "ga_measurement_id", label: "Google Analytics 4 – ID misurazione (es. G-XXXXXXXXXX)" },
];

export default function AdminDashboard() {
  const { user, checked, logout } = useAuth();
  const { reload } = useData();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => { if (checked && !user) navigate("/admin"); }, [checked, user, navigate]);

  const loadTab = useCallback(async (t) => {
    setLoading(true);
    try {
      if (t === "settings") setSettings((await api.get("/settings")).data);
      else if (t === "contacts") setContacts((await api.get("/admin/contact-requests")).data);
      else if (t === "stats") setStats((await api.get("/admin/stats")).data);
      else setItems((await api.get(`/content/${t}`)).data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user) loadTab(tab); }, [tab, user, loadTab]);

  const saveItem = async () => {
    try {
      const payload = { ...editing };
      const id = payload.id;
      if (id && items.some((i) => i.id === id)) {
        await api.put(`/admin/content/${tab}/${id}`, payload);
      } else {
        await api.post(`/admin/content/${tab}`, payload);
      }
      toast.success("Salvato");
      setEditing(null);
      await loadTab(tab); await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio fallito"); }
  };

  const delItem = async (id) => {
    if (!window.confirm("Eliminare questo elemento?")) return;
    try { await api.delete(`/admin/content/${tab}/${id}`); toast.success("Eliminato"); await loadTab(tab); await reload(); }
    catch { toast.error("Eliminazione fallita"); }
  };

  const delContact = async (id) => {
    if (!window.confirm("Eliminare questa richiesta?")) return;
    try { await api.delete(`/admin/contact-requests/${id}`); await loadTab("contacts"); } catch { toast.error("Errore"); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try { await api.put("/admin/settings", settings); toast.success("Impostazioni salvate"); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setSavingSettings(false); }
  };

  if (!checked || !user) return <div className="min-h-screen bg-av-black" />;

  const schema = SCHEMAS[tab];
  const TABS = ["stats", ...Object.keys(SCHEMAS), "settings", "contacts"];
  const tabLabel = (t) => t === "settings" ? "Impostazioni" : t === "contacts" ? "Richieste" : t === "stats" ? "Statistiche" : SCHEMAS[t].label;

  return (
    <div className="min-h-screen bg-av-black text-av-white flex flex-col">
      <Seo title="Gestione" path="/admin/dashboard" />
      <header className="border-b border-white/10 px-5 sm:px-8 h-16 flex items-center justify-between sticky top-0 bg-av-black/90 backdrop-blur z-20">
        <Logo className="h-8" to={null} />
        <div className="flex items-center gap-4">
          <Link to="/" target="_blank" className="text-sm text-av-gray hover:text-av-lime inline-flex items-center gap-1.5"><ExternalLink size={14} /> Vedi il sito</Link>
          <button onClick={() => { logout(); navigate("/admin"); }} data-testid="admin-logout" className="text-sm text-av-gray hover:text-red-400 inline-flex items-center gap-1.5"><LogOut size={14} /> Esci</button>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row">
        <aside className="lg:w-60 border-b lg:border-b-0 lg:border-r border-white/10 p-3 lg:p-4 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t} onClick={() => { setTab(t); setEditing(null); }} data-testid={`admin-tab-${t}`}
                    className={`text-left whitespace-nowrap rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${tab === t ? "bg-av-bright text-av-black" : "text-av-white/75 hover:bg-white/5"}`}>
              {tabLabel(t)}
            </button>
          ))}
        </aside>

        <main className="flex-1 p-5 sm:p-8 min-w-0">
          {loading && <div className="flex items-center gap-2 text-av-gray"><Loader2 size={16} className="animate-spin" /> Caricamento…</div>}

          {/* STATS */}
          {tab === "stats" && stats && !loading && (
            <div data-testid="admin-stats">
              <h1 className="font-display text-3xl mb-2">Statistiche del sito</h1>
              <p className="text-sm text-av-gray mb-8">Dati aggregati e anonimi raccolti internamente (nessun dato personale). Configura Google Analytics dalle Impostazioni per analisi più avanzate.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  ["Visite pagine", stats.totali.visite],
                  ["Richieste ricevute", stats.richieste],
                  ["Form inviati", stats.totali.form_inviati],
                  ["Click WhatsApp", stats.totali.whatsapp],
                  ["Click telefono", stats.totali.telefono],
                  ["Guide visualizzate", stats.totali.guide_viste],
                  ["Ricerche effettuate", stats.totali.ricerche],
                  ["Ricerche senza risultato", stats.totali.ricerche_senza_risultato],
                  ["Diagnostica avviata", stats.totali.diagnostica],
                ].map(([label, val]) => (
                  <div key={label} data-testid={`stat-${label}`} className="rounded-2xl border border-white/10 p-5 bg-av-green-dark/20" style={{ backgroundColor: "rgba(22,56,29,0.2)" }}>
                    <p className="text-3xl font-display text-av-lime">{val ?? 0}</p>
                    <p className="mt-1 text-xs text-av-gray">{label}</p>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-6 mt-10">
                <div className="rounded-2xl border border-white/10 p-6">
                  <h2 className="font-display text-xl mb-4">Guide più visualizzate</h2>
                  {stats.top_guide?.length ? (
                    <ul className="space-y-2.5">
                      {stats.top_guide.map((g, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-av-white/85 truncate">{g.titolo}</span>
                          <span className="text-av-lime font-semibold shrink-0">{g.count}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-av-gray">Ancora nessun dato.</p>}
                </div>
                <div className="rounded-2xl border border-white/10 p-6">
                  <h2 className="font-display text-xl mb-4">Ricerche senza risultato</h2>
                  <p className="text-xs text-av-gray mb-4">Spunti per nuove guide da pubblicare.</p>
                  {stats.ricerche_senza_risultato?.length ? (
                    <ul className="space-y-2.5">
                      {stats.ricerche_senza_risultato.map((r, i) => (
                        <li key={i} className="flex items-center justify-between gap-3 text-sm">
                          <span className="text-av-white/85 truncate">"{r.query}"</span>
                          <span className="text-av-gray shrink-0">{r.count}×</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-av-gray">Nessuna ricerca a vuoto registrata.</p>}
                </div>
              </div>
            </div>
          )}

          {/* SETTINGS */}
          {tab === "settings" && settings && !loading && (
            <div className="max-w-3xl">
              <h1 className="font-display text-3xl mb-2">Impostazioni & dati di contatto</h1>
              <p className="text-sm text-av-gray mb-8">Modifica i testi principali e i dati di contatto del sito.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                {SETTINGS_FIELDS.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                    <AdminField field={f} value={settings[f.key]} onChange={(v) => setSettings((s) => ({ ...s, [f.key]: v }))} />
                  </div>
                ))}
              </div>
              <button onClick={saveSettings} disabled={savingSettings} data-testid="admin-save-settings"
                      className="mt-8 inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-7 py-3 text-sm font-semibold hover:bg-av-lime transition-colors disabled:opacity-60">
                {savingSettings ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salva impostazioni
              </button>
            </div>
          )}

          {/* CONTACTS */}
          {tab === "contacts" && !loading && (
            <div className="max-w-4xl">
              <h1 className="font-display text-3xl mb-6">Richieste di contatto</h1>
              {contacts.length === 0 ? (
                <div className="flex flex-col items-center py-20 text-av-gray"><Inbox size={40} className="mb-4 opacity-50" /> Nessuna richiesta ricevuta.</div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((c) => (
                    <div key={c.id} data-testid={`contact-req-${c.id}`} className="rounded-2xl border border-white/10 p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-av-white">{c.nome} · <span className="text-av-lime">{c.tipologia}</span></h3>
                          <p className="text-sm text-av-gray mt-1">{c.telefono} · {c.email} {c.comune && `· ${c.comune}`}</p>
                        </div>
                        <button onClick={() => delContact(c.id)} data-testid={`contact-delete-${c.id}`} className="text-av-gray hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full bg-av-bright/15 text-av-lime px-2.5 py-0.5 text-[11px] font-semibold">{c.stato || "Nuova"}</span>
                        {c.preferenza_contatto && <span className="rounded-full bg-white/10 text-av-white/80 px-2.5 py-0.5 text-[11px]">Preferenza: {c.preferenza_contatto}</span>}
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] ${c.email_inviata ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>{c.email_inviata ? "Email inviata ✓" : "Email non inviata"}</span>
                      </div>
                      <p className="mt-3 text-sm text-av-white/85">{c.descrizione}</p>
                      {c.allegati?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {c.allegati.map((a, i) => <a key={i} href={resolveImg(a)} target="_blank" rel="noreferrer"><img src={resolveImg(a)} alt="" className="h-16 rounded border border-white/15" /></a>)}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-3 items-center">
                        <a href={`tel:${c.telefono}`} className="text-xs text-av-lime hover:underline">Chiama</a>
                        <a href={`mailto:${c.email}`} className="text-xs text-av-lime hover:underline">Email</a>
                        <span className="text-xs text-av-gray/60">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                        {c.pagina_provenienza && <span className="text-xs text-av-gray/50 truncate max-w-[240px]">da: {c.pagina_provenienza}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* COLLECTIONS */}
          {schema && !loading && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="font-display text-3xl">{schema.label}</h1>
                <button onClick={() => setEditing({})} data-testid="admin-add-item" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-5 py-2.5 text-sm font-semibold hover:bg-av-lime transition-colors"><Plus size={16} /> Aggiungi</button>
              </div>
              <div className="grid gap-3">
                {items.map((it) => (
                  <div key={it.id} data-testid={`admin-item-${it.id}`} className="flex items-center gap-4 rounded-xl border border-white/10 p-4">
                    {(it.immagine || it.prima) && <img src={resolveImg(it.immagine || it.prima)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-av-white truncate">{it[schema.titleKey] || "(senza titolo)"}</p>
                      <p className="text-xs text-av-gray truncate">{tab === "local-pages" ? it.slug : (it.categoria || it.comune || it.servizio || it.slug || "")}{it.placeholder ? " · demo" : ""}{it.pubblicato === false ? " · bozza" : ""}</p>
                    </div>
                    <button onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`} className="text-av-gray hover:text-av-lime"><Pencil size={16} /></button>
                    <button onClick={() => delItem(it.id)} data-testid={`admin-delete-${it.id}`} className="text-av-gray hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                ))}
                {items.length === 0 && <p className="text-av-gray text-sm">Nessun elemento.</p>}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* EDIT MODAL */}
      {editing && schema && (
        <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="relative bg-av-black border border-white/15 rounded-2xl w-full max-w-2xl my-8 p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="admin-edit-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">{editing.id ? "Modifica" : "Nuovo elemento"}</h2>
              <button onClick={() => setEditing(null)} className="text-av-gray hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 max-h-[65vh] overflow-y-auto pr-1">
              {schema.fields.map((f) => (
                <div key={f.key} className={["textarea", "list", "image"].includes(f.type) ? "sm:col-span-2" : ""}>
                  <AdminField field={f} value={editing[f.key]} onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))} />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="rounded-full border border-white/20 px-5 py-2.5 text-sm">Annulla</button>
              <button onClick={saveItem} data-testid="admin-save-item" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-2.5 text-sm font-semibold hover:bg-av-lime transition-colors"><Save size={15} /> Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
